/**
 * Web push notification service for Gravity Station.
 *
 * Handles browser push subscription management and preference updates.
 * Subscriptions are identified by a unique ID, not tied to wallet addresses.
 * The subscription ID must be stored locally by the client.
 */

import { api } from './api';
import type {
  NotificationPreferences,
  PreferencesResponse,
  SubscribeResponse,
  SuccessResponse,
  VapidPublicKeyResponse,
} from '@/types/notifications';

/** Local storage key for the subscription ID */
const SUBSCRIPTION_ID_KEY = 'gravity_station_push_subscription_id';

/**
 * Get the stored subscription ID from local storage.
 */
export function getStoredSubscriptionId(): string | null {
  return localStorage.getItem(SUBSCRIPTION_ID_KEY);
}

/**
 * Store the subscription ID in local storage.
 */
function setStoredSubscriptionId(id: string): void {
  localStorage.setItem(SUBSCRIPTION_ID_KEY, id);
}

/**
 * Remove the subscription ID from local storage.
 */
function clearStoredSubscriptionId(): void {
  localStorage.removeItem(SUBSCRIPTION_ID_KEY);
}

/**
 * Convert a base64 URL-safe string to a Uint8Array.
 * Required for the applicationServerKey in push subscription.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

/**
 * Get the VAPID public key from the backend.
 */
export async function getVapidPublicKey(): Promise<string | null> {
  try {
    const response = await api.get<VapidPublicKeyResponse>('/api/vapid_public');
    return response.public_key;
  } catch (error) {
    console.error('Failed to get VAPID public key:', error);
    return null;
  }
}

/**
 * Check if web push notifications are supported by the browser.
 */
export function isPushSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Get the current notification permission status.
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Request notification permission from the user.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return await Notification.requestPermission();
}

/**
 * Check if the current browser has an active push subscription.
 */
export async function isSubscriptionActive(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch {
    return false;
  }
}

/**
 * Get the current push subscription if one exists.
 */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return null;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Subscribe the browser to push notifications.
 *
 * @param preferences - Optional initial preferences (defaults to all enabled)
 * @returns The subscription ID if successful, null otherwise
 */
export async function subscribeToNotifications(
  preferences?: NotificationPreferences
): Promise<string | null> {
  // Check browser support
  if (!isPushSupported()) {
    console.warn('Push notifications not supported by this browser');
    return null;
  }

  // Request permission
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    console.warn('Notification permission denied');
    return null;
  }

  try {
    // Get VAPID key from backend
    const vapidKey = await getVapidPublicKey();
    if (vapidKey === null || vapidKey === '') {
      console.error('Failed to get VAPID public key');
      return null;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register('/serviceWorker.js', {
      scope: '/',
    });

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Create new subscription
      // Note: Firefox requires the Uint8Array directly, not .buffer
      // Chrome accepts either, so we use Uint8Array for compatibility
      // Using .slice() creates a fresh Uint8Array with ArrayBuffer (not ArrayBufferLike)
      // which satisfies both TypeScript and browser compatibility
      const applicationServerKey = urlBase64ToUint8Array(vapidKey).slice();
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    // Extract subscription data in the format the backend expects
    // The browser's toJSON() returns keys as Record<string, string> which may not
    // have the exact structure we need. We explicitly extract the required fields.
    const subscriptionJson = subscription.toJSON();
    const keys = subscriptionJson.keys;
    const endpoint = subscriptionJson.endpoint;
    const p256dh = keys?.p256dh;
    const auth = keys?.auth;
    
    if (
      endpoint === undefined ||
      endpoint === null ||
      keys === undefined ||
      keys === null ||
      p256dh === undefined ||
      p256dh === null ||
      auth === undefined ||
      auth === null
    ) {
      console.error('Invalid push subscription: missing required fields', subscriptionJson);
      return null;
    }

    const subscriptionData = {
      endpoint,
      keys: {
        p256dh,
        auth,
      },
    };

    // Send subscription to backend and get the subscription ID
    const response = await api.post<SubscribeResponse>('/api/push/subscribe', {
      subscription: subscriptionData,
      preferences,
    });

    // Store the subscription ID locally
    setStoredSubscriptionId(response.subscription_id);

    console.log('Successfully subscribed to push notifications, ID:', response.subscription_id);
    return response.subscription_id;
  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error);
    return null;
  }
}

/**
 * Unsubscribe the browser from push notifications.
 *
 * @returns true if unsubscription was successful
 */
export async function unsubscribeFromNotifications(): Promise<boolean> {
  try {
    const subscriptionId = getStoredSubscriptionId();
    
    // Unsubscribe from browser
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    }

    // Remove from backend if we have a subscription ID
    if (subscriptionId !== null && subscriptionId !== '') {
      await api.post<SuccessResponse>('/api/push/unsubscribe', {
        subscription_id: subscriptionId,
      });
    }

    // Clear local storage
    clearStoredSubscriptionId();

    console.log('Successfully unsubscribed from push notifications');
    return true;
  } catch (error) {
    console.error('Failed to unsubscribe from push notifications:', error);
    return false;
  }
}

/**
 * Get notification preferences for the current subscription.
 *
 * @returns Current preferences or null if not subscribed or on error
 */
export async function getPreferences(): Promise<PreferencesResponse | null> {
  const subscriptionId = getStoredSubscriptionId();
  if (subscriptionId === null || subscriptionId === '') {
    return null;
  }

  try {
    return await api.get<PreferencesResponse>('/api/push/preferences', {
      subscription_id: subscriptionId,
    });
  } catch (error) {
    console.error('Failed to get notification preferences:', error);
    return null;
  }
}

/**
 * Update notification preferences for the current subscription.
 *
 * @param preferences - New preferences to save
 * @returns true if update was successful
 */
export async function updatePreferences(preferences: NotificationPreferences): Promise<boolean> {
  const subscriptionId = getStoredSubscriptionId();
  if (subscriptionId === null || subscriptionId === '') {
    console.error('No subscription ID found');
    return false;
  }

  try {
    await api.put<SuccessResponse>('/api/push/preferences', {
      subscription_id: subscriptionId,
      preferences,
    });
    console.log('Successfully updated notification preferences');
    return true;
  } catch (error) {
    console.error('Failed to update notification preferences:', error);
    return false;
  }
}

/**
 * Convert USD amount to cents for storage.
 */
export function usdToCents(usd: number | null): number | null {
  if (usd === null) return null;
  return Math.round(usd * 100);
}

/**
 * Convert cents to USD for display.
 */
export function centsToUsd(cents: number | null): number | null {
  if (cents === null) return null;
  return cents / 100;
}
